'use client'

import { useCallback, useEffect } from 'react'
import { signIn } from 'next-auth/react'
import { landingMarkup } from './landing/landing-markup'

const GOOGLE_LOGIN_SELECTOR = '[loginwithgoogle]'
const LOCATION_SELECTOR = '[data-location-text]'
const TELEPORT_SCRIPT_ID = 'teleporthq-custom-scripts'
const TELEPORT_SCRIPT_SRC = 'https://unpkg.com/@teleporthq/teleport-custom-scripts'

const LOCATION_WORDS = [
  'Ericeira',
  'Lisbon',
  'Peniche',
  'Porto',
  'Uluwatu',
  'Canggu',
  'Weligama',
  'Puerto Escondido',
  'Santa Teresa',
  'Santa Cruz',
  'Malibu',
]

const LOCATION_TYPE_DELAY = 120
const LOCATION_DELETE_DELAY = 60
const LOCATION_HOLD_DELAY = 1600

type TeleportWindow = Window & {
  TeleportHQ?: {
    init?: () => void
    refresh?: () => void
  }
  initTeleport?: () => void
}

export default function SurfslotLanding() {
  const handleGoogleSignIn = useCallback(() => {
    void signIn('google')
  }, [])

  useEffect(() => {
    const buttons = document.querySelectorAll<HTMLElement>(GOOGLE_LOGIN_SELECTOR)
    const handleClick = (event: Event) => {
      event.preventDefault()
      handleGoogleSignIn()
    }

    buttons.forEach((element) => element.addEventListener('click', handleClick))
    return () => {
      buttons.forEach((element) =>
        element.removeEventListener('click', handleClick)
      )
    }
  }, [handleGoogleSignIn])

  useEffect(() => {
    const locationElements = document.querySelectorAll<HTMLElement>(LOCATION_SELECTOR)
    if (!locationElements.length) {
      return
    }

    let wordIndex = 0
    let charIndex = LOCATION_WORDS[0].length
    let isDeleting = true
    let timeoutId: number | undefined

    const updateElements = (value: string) => {
      locationElements.forEach((element) => {
        element.textContent = value
      })
    }

    const type = () => {
      const currentWord = LOCATION_WORDS[wordIndex]

      if (isDeleting) {
        charIndex = Math.max(charIndex - 1, 0)
      } else if (charIndex < currentWord.length) {
        charIndex += 1
      }

      updateElements(currentWord.slice(0, charIndex))

      if (!isDeleting && charIndex === currentWord.length) {
        timeoutId = window.setTimeout(() => {
          isDeleting = true
          type()
        }, LOCATION_HOLD_DELAY)
        return
      }

      if (isDeleting && charIndex === 0) {
        isDeleting = false
        wordIndex = (wordIndex + 1) % LOCATION_WORDS.length
      }

      const delay = isDeleting ? LOCATION_DELETE_DELAY : LOCATION_TYPE_DELAY
      timeoutId = window.setTimeout(type, delay)
    }

    timeoutId = window.setTimeout(type, LOCATION_HOLD_DELAY)

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [])

  useEffect(() => {
    const runTeleportInit = () => {
      const teleportWindow = window as TeleportWindow
      teleportWindow.TeleportHQ?.init?.()
      teleportWindow.TeleportHQ?.refresh?.()
      teleportWindow.initTeleport?.()
    }

    const existingScript = document.getElementById(
      TELEPORT_SCRIPT_ID
    ) as HTMLScriptElement | null

    if (existingScript) {
      if (existingScript.dataset.loaded === 'true') {
        runTeleportInit()
        return
      }

      existingScript.addEventListener('load', runTeleportInit)
      return () => {
        existingScript.removeEventListener('load', runTeleportInit)
      }
    }

    const script = document.createElement('script')
    script.id = TELEPORT_SCRIPT_ID
    script.src = TELEPORT_SCRIPT_SRC
    script.defer = true

    const handleScriptLoad = () => {
      script.dataset.loaded = 'true'
      runTeleportInit()
    }

    script.addEventListener('load', handleScriptLoad)
    document.body.appendChild(script)

    return () => {
      script.removeEventListener('load', handleScriptLoad)
    }
  }, [])

  return (
    <div dangerouslySetInnerHTML={{ __html: landingMarkup }} />
  )
}
